import type { TreeWithCourses } from "src/shared/api-types/certificate";
import type { RelationTreeRoleNode } from "src/shared/api-types/course";

import { hasNoUserProperties } from "./hasNoUserProperties";

/**
 * Per-role profile-setup detection (CREW-530).
 *
 * "A role requires a Position/Operation" is not modeled anywhere, so the crew
 * relation catalog (GET /courses/relation-tree) acts as the requires-proxy:
 * a role requires positions when the catalog tree defines at least one
 * (content-bearing) position under it, and an assigned position requires
 * operations when its catalog counterpart carries at least one.
 *
 * The catalog is fetched best-effort: when it is unavailable (null), detection
 * falls back to the original whole-profile heuristic (hasNoUserProperties) so
 * the feature degrades instead of breaking.
 */

export interface CatalogRoleRequirements {
  hasPositions: boolean;
  operationCountByPositionId: Map<number, number>;
}

export type CatalogRequirements = Map<number, CatalogRoleRequirements>;

export const buildCatalogRequirements = (
  tree: RelationTreeRoleNode[] | null | undefined,
): CatalogRequirements | null => {
  if (!tree) return null;

  return new Map(
    tree.map((role) => [
      role.id,
      {
        hasPositions: role.positions.length > 0,
        operationCountByPositionId: new Map(
          role.positions.map((position) => [position.id, position.operations.length]),
        ),
      },
    ]),
  );
};

export type IncompleteReason = "missing_positions" | "missing_operations";

/** Prop shape the compliance tabs receive to render per-role warnings. */
export interface ProfileSetupHints {
  incompleteRoles: IncompleteRole[];
  isSelfView: boolean;
  onEditProperties: () => void;
}

export interface IncompletePosition {
  id: number;
  name: string;
}

export interface IncompleteRole {
  roleId: number;
  reason: IncompleteReason;
  /** The assigned positions lacking required operations (empty for missing_positions). */
  positionsMissingOperations: IncompletePosition[];
}

/**
 * A role is incomplete when the catalog says it requires positions but the
 * user has none assigned under it, or when one of the user's assigned
 * positions has no operations while its catalog position defines some.
 * Roles absent from the catalog require nothing. The offending positions are
 * named so the warnings can say exactly what failed.
 */
export const getIncompleteRoles = (
  relationTree: TreeWithCourses | null | undefined,
  requirements: CatalogRequirements,
): IncompleteRole[] => {
  const roles = relationTree?.roles ?? [];

  return roles.flatMap((role): IncompleteRole[] => {
    const catalogRole = requirements.get(role.id);
    if (!catalogRole) return [];

    if (catalogRole.hasPositions && role.positions.length === 0) {
      return [{ roleId: role.id, reason: "missing_positions", positionsMissingOperations: [] }];
    }

    const positionsMissingOperations = role.positions
      .filter(
        (position) =>
          position.operations.length === 0 && (catalogRole.operationCountByPositionId.get(position.id) ?? 0) > 0,
      )
      .map((position) => ({ id: position.id, name: position.name }));

    if (positionsMissingOperations.length) {
      return [{ roleId: role.id, reason: "missing_operations", positionsMissingOperations }];
    }

    return [];
  });
};

/**
 * Whole-profile flag driving the info box and the members-list mark.
 * With catalog data: incomplete when ANY role is incomplete (per-role
 * semantics). Without it (requirements === null): the legacy all-roles-empty
 * heuristic.
 */
export const isProfileSetupIncomplete = (
  relationTree: TreeWithCourses | null | undefined,
  requirements: CatalogRequirements | null,
): boolean => {
  if (!requirements) return hasNoUserProperties(relationTree);
  return getIncompleteRoles(relationTree, requirements).length > 0;
};
